// ===== HOOK: DADOS DO DASHBOARD =====

import { useState, useEffect, useCallback } from "react";
import { DashboardData } from "../types";
import { createAPIService } from "../services/api-service";

/**
 * Hook para gerenciar dados do dashboard
 */
export const useDashboardData = (
  apiBaseUrl: string = "http://localhost:8000",
  apiToken?: string | null
) => {
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(
    null
  );
  const [alunosRiscoData, setAlunosRiscoData] = useState<any[]>([]);
  const [turmasData, setTurmasData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchDashboardData = useCallback(async () => {
    if (!apiToken) return;

    setLoading(true);
    setError(null);

    try {
      const { dashboardAPI } = createAPIService(apiBaseUrl, apiToken);

      // Busca dados completos do dashboard
      const data = await dashboardAPI.getOverview();
      setDashboardData(data);

      // Extrai dados de alunos em risco se disponível
      if (data.at_risk_students) {
        setAlunosRiscoData(data.at_risk_students);
      }

      // Extrai dados de turmas se disponível
      if (data.classes) {
        setTurmasData(data.classes);
      }
    } catch (err) {
      const errorMsg =
        err instanceof Error ? err.message : "Erro ao buscar dados do dashboard";
      console.error("Erro ao buscar dados do dashboard:", errorMsg);
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  }, [apiBaseUrl, apiToken]);

  // Buscar dados quando o token mudar
  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  const fetchAlunosRisco = useCallback(async () => {
    if (!apiToken) return;

    try {
      const { dashboardAPI } = createAPIService(apiBaseUrl, apiToken);
      const data = await dashboardAPI.getAtRiskStudents();
      setAlunosRiscoData(data.data || []);
    } catch (err) {
      console.error("Erro ao buscar alunos em risco:", err);
    }
  }, [apiBaseUrl, apiToken]);

  const fetchTurmas = useCallback(async () => {
    if (!apiToken) return;

    try {
      const { dashboardAPI } = createAPIService(apiBaseUrl, apiToken);
      const data = await dashboardAPI.getClasses();
      setTurmasData(data.data || []);
    } catch (err) {
      console.error("Erro ao buscar turmas:", err);
    }
  }, [apiBaseUrl, apiToken]);

  return {
    dashboardData,
    alunosRiscoData,
    turmasData,
    loading,
    error,
    refetch: {
      fetchDashboardData,
      fetchAlunosRisco,
      fetchTurmas,
    },
  };
};
